import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MagnifyingGlass, Plus, X, User, Buildings, Briefcase, UsersThree } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ContactSearch — composant réutilisable pour rechercher des contacts existants.
 *
 * Props :
 *  - sources: array of 'equipe' | 'entreprises' | 'users' | 'marches' | 'proprietaires'
 *  - chantierId: required for 'equipe', 'marches', 'proprietaires'
 *  - placeholder: string
 *  - onSelect: (contact) => void — appelé quand l'utilisateur choisit un résultat
 *  - onCreateNew: (query) => void — optionnel, affiche un bouton "Créer"
 *  - value: displayed text (controlled mode)
 *  - userRoleFilter: string, used with 'users' source (e.g. 'client', 'entreprise')
 *  - autoFocus: bool
 *  - testId: string
 *
 * Contact shape: { id, name, detail, email, telephone, source, raw }
 */
export default function ContactSearch({
  sources = ['equipe', 'entreprises', 'users'],
  chantierId,
  placeholder = 'Rechercher…',
  onSelect,
  onCreateNew,
  value,
  userRoleFilter,
  autoFocus,
  testId = 'contact-search',
}) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { if (value !== undefined) setQuery(value); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const runSearch = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const all = [];
      const calls = [];

      if (sources.includes('equipe') && chantierId) {
        calls.push(
          axios.get(`${API_URL}/api/projets/${chantierId}/moe`, { withCredentials: true })
            .then(({ data }) => (data || [])
              .filter(m => ((m.name || '') + (m.email || '')).toLowerCase().includes(q.toLowerCase()))
              .forEach(m => all.push({
                id: m.id || m._id,
                name: m.name,
                detail: m.mission || m.role || '',
                email: m.email,
                telephone: m.telephone,
                source: 'equipe',
                raw: m,
              })))
            .catch(() => {})
        );
      }

      if (sources.includes('entreprises')) {
        calls.push(
          axios.get(`${API_URL}/api/entreprises/search?q=${encodeURIComponent(q)}`, { withCredentials: true })
            .then(({ data }) => (data || []).forEach(e => all.push({
              id: e.id || e._id,
              name: e.name || e.nom || e.company,
              detail: e.specialite || (Array.isArray(e.specialites) ? e.specialites.join(', ') : '') || 'Entreprise',
              email: e.email,
              telephone: e.telephone,
              source: 'entreprise',
              raw: e,
            })))
            .catch(() => {})
        );
      }

      if (sources.includes('users')) {
        const roleParam = userRoleFilter ? `&role=${encodeURIComponent(userRoleFilter)}` : '';
        calls.push(
          axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(q)}${roleParam}`, { withCredentials: true })
            .then(({ data }) => (data || []).forEach(u => all.push({
              id: u.id || u._id,
              name: u.name,
              detail: u.role || 'Contact',
              email: u.email,
              telephone: u.telephone,
              source: 'user',
              raw: u,
            })))
            .catch(() => {})
        );
      }

      if (sources.includes('marches') && chantierId) {
        calls.push(
          axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
            .then(({ data }) => (data || [])
              .filter(l => (l.entreprise_nom || l.entreprise_name || '').toLowerCase().includes(q.toLowerCase()))
              .forEach(l => {
                if (!l.entreprise_nom && !l.entreprise_name) return;
                all.push({
                  id: l.id || l._id || `${l.code}-${l.entreprise_nom}`,
                  name: l.entreprise_nom || l.entreprise_name,
                  detail: `Lot ${l.code || '?'} — ${l.nom || ''}`,
                  email: l.entreprise_email,
                  telephone: l.entreprise_telephone,
                  source: 'marche',
                  raw: l,
                });
              }))
            .catch(() => {})
        );
      }

      if (sources.includes('proprietaires') && chantierId) {
        calls.push(
          axios.get(`${API_URL}/api/chantiers/${chantierId}`, { withCredentials: true })
            .then(({ data }) => {
              const list = data.proprietaires || (data.owner_name ? [{ nom: data.owner_name, email: data.owner_email, telephone: data.owner_phone }] : []);
              list.filter(p => ((p.nom || '') + (p.email || '')).toLowerCase().includes(q.toLowerCase()))
                .forEach(p => all.push({
                  id: p.id || p.email || p.nom,
                  name: p.nom || p.name,
                  detail: 'Propriétaire',
                  email: p.email,
                  telephone: p.telephone,
                  source: 'proprietaire',
                  raw: p,
                }));
            })
            .catch(() => {})
        );
      }

      await Promise.all(calls);

      // Dédup par email|nom
      const seen = new Set();
      const uniq = [];
      for (const item of all) {
        const key = (item.email || '') + '|' + (item.name || '');
        if (!seen.has(key) && item.name) {
          seen.add(key);
          uniq.push(item);
        }
      }
      setResults(uniq.slice(0, 20));
    } finally { setLoading(false); }
  };

  const onChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 200);
  };

  const pick = (r) => {
    setQuery(r.name);
    setResults([]);
    setOpen(false);
    onSelect?.(r);
  };

  const sourceLabel = (s) => ({
    equipe: 'MOE',
    entreprise: 'Entreprise',
    user: 'Contact',
    marche: 'Marché',
    proprietaire: 'Client',
  }[s] || 'Autre');

  const sourceIcon = (s) => {
    const Icon = ({ entreprise: Buildings, marche: Briefcase, equipe: UsersThree, proprietaire: User, user: User }[s]) || User;
    return <Icon size={13} className="text-[#9A9A9A]" />;
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A9A9A]" />
        <input
          type="text"
          className="w-full pl-8 pr-8 py-2 border border-[#E0E0DB] text-sm focus:outline-none focus:border-[#2C2C2C]"
          placeholder={placeholder}
          value={query}
          onChange={onChange}
          onFocus={() => setOpen(true)}
          autoFocus={autoFocus}
          data-testid={`${testId}-input`}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); onSelect?.(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9A9A9A] hover:text-[#2C2C2C]"
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E0E0DB] shadow-lg z-50 max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-[#9A9A9A]">Recherche…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3">
              <p className="text-xs text-[#9A9A9A]">Aucun résultat pour « {query} »</p>
              {onCreateNew && (
                <button
                  type="button"
                  onClick={() => { onCreateNew(query); setOpen(false); }}
                  className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#C4773B] hover:underline"
                  data-testid={`${testId}-create`}
                >
                  <Plus size={12} weight="bold" /> Créer « {query} »
                </button>
              )}
            </div>
          )}
          {!loading && results.map(r => (
            <button
              key={`${r.source}-${r.id}`}
              type="button"
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-[#FAFAF8] border-b border-[#F0F0ED] last:border-b-0 flex items-center justify-between gap-2"
              data-testid={`${testId}-result-${r.id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {sourceIcon(r.source)}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2C2C2C] truncate">{r.name}</p>
                  <p className="text-[11px] text-[#6B6B6B] truncate">
                    {r.detail}{r.email ? ` — ${r.email}` : ''}
                  </p>
                </div>
              </div>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-[#9A9A9A] px-1.5 py-0.5 bg-[#F5F5F3] flex-shrink-0">
                {sourceLabel(r.source)}
              </span>
            </button>
          ))}
          {!loading && results.length > 0 && onCreateNew && (
            <button
              type="button"
              onClick={() => { onCreateNew(query); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-[#C4773B] bg-[#FDF6EC] hover:bg-[#F9ECD8] flex items-center gap-1"
              data-testid={`${testId}-create-bottom`}
            >
              <Plus size={12} weight="bold" /> Ajouter un nouveau contact « {query} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}
